/**
 * Applies global Highcharts options and configures offline export for all charts.
 * Import this once at app bootstrap (e.g. in init-apps entry) so all charts use it.
 *
 * Export is always performed client-side (no calls to export.highcharts.com).
 * Export uses a generic font (sans-serif) so PNG export does not depend on web
 * fonts and avoids canvas taint that would trigger Highcharts error #28.
 *
 * Menu (export/print button): set REACT_APP_HIGHCHARTS_MENU_ENABLED='false' to disable.
 * When unset, the menu is enabled by default (opt-out). Deployments that must comply
 * with Highcharts Fair Use (e.g. hide export by default) should set the env to 'false'.
 * The menu setting is read once when this module is loaded (no runtime toggle).
 *
 * Applies to both 'highcharts' and 'highcharts/highstock' so device, report, insight,
 * spaces, and building-insight charts all get the same behaviour.
 */
import Highcharts from 'highcharts';
import Highstock from 'highcharts/highstock';
import HighchartsExporting from 'highcharts/modules/exporting';
import HighchartsOfflineExporting from 'highcharts/modules/offline-exporting';
import config from './config';

// NOTE: GraphConfig.ts (business-dashboard reports) also initializes exporting for the PDF path.
// Highcharts modules are safe to initialize multiple times; this file is the canonical global setup.

/**
 * Called when client-side export fails (e.g. chart with embedded images, or browser limits).
 * Prevents uncaught Highcharts error #28 and gives the user a clear message.
 */
function onExportError(_exportingOptions: Highcharts.ExportingOptions, err: Error & { message?: string }): void {
    const userMessage = 'Chart export failed. Try a different format (e.g. SVG) or a simpler view.';
    if (typeof window !== 'undefined' && window.console?.error) {
        window.console.error('[Highcharts export]', userMessage, err);
    }
    if (typeof window !== 'undefined' && window.alert) {
        // eslint-disable-next-line no-alert -- intentional: Highcharts export error callback needs to inform user
        window.alert(`Export failed. ${userMessage}`);
    }
}

/**
 * Chart options applied only during export. Using a generic font and no external
 * resources avoids canvas taint (CORS) so local PNG export can succeed when
 * the live chart uses web fonts (e.g. OpenSans-Regular) or would otherwise
 * load external assets during SVG→canvas rendering.
 */
const exportOnlyChartOptions: Highcharts.Options = {
    chart: {
        style: {
            fontFamily: 'sans-serif',
        },
    },
};

/** Export menu: only Print, PNG and SVG (no full screen, no JPEG). */
const exportMenuItems: Highcharts.ExportingMenuObject[] = ['printChart', 'separator', 'downloadPNG', 'downloadSVG'];

const isJest = typeof process !== 'undefined' && process.env.JEST_WORKER_ID != null;
if (typeof window !== 'undefined' && !isJest) {
    const exportingOptions = {
        accessibility: { enabled: false },
        exporting: {
            enabled: config.highchartsMenuEnabled,
            fallbackToExportServer: false,
            allowHTML: false,
            error: onExportError,
            chartOptions: exportOnlyChartOptions,
            buttons: {
                contextButton: {
                    menuItems: exportMenuItems,
                },
            },
        },
    };
    HighchartsExporting(Highcharts);
    HighchartsOfflineExporting(Highcharts);
    Highcharts.setOptions(exportingOptions);
    HighchartsExporting(Highstock);
    HighchartsOfflineExporting(Highstock);
    Highstock.setOptions(exportingOptions);
}
